import React from 'react';
import { Box, Chip, Typography, Grid, Tooltip } from '@mui/material';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import LaunchIcon from '@mui/icons-material/Launch';
import { BaseCard } from '../common/BaseCard';
import { formatDateSafe, formatDocument, cleanDocument } from '../utils/formatters';
import { useParams } from 'react-router-dom';
import { IConsultaParticipacaoEmpresarial } from '../../../../../../application/models/dossie';
import { useDossieService } from '../../../../../../use-cases/dossie';

export const ParticipacaoEmpresarialCard = ({
  participacaoEmpresarial,
}: {
  participacaoEmpresarial: IConsultaParticipacaoEmpresarial[] | null;
}) => {
  const { solicitarConsultaComCobranca } = useDossieService();
  const { documento: documentoOrigem } = useParams<{ documento: string }>();
  const participacoesList = participacaoEmpresarial || [];

  if (participacoesList.length === 0) return null;

  return (
    <BaseCard
      title="Participação Empresarial"
      icon={<WorkOutlineIcon />}
      canSplit={participacoesList.length > 2}
    >
      <Box
        className="socio-grid-container"
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: participacoesList.length > 1 ? '1fr 1fr' : '1fr' },
          gap: 2.5,
        }}
      >
        {participacoesList.map((empresa: IConsultaParticipacaoEmpresarial, idx: number) => {
          const docEmpresa = empresa.Documento;
          const cleanDoc = cleanDocument(docEmpresa);
          const isDocPF = cleanDoc ? cleanDoc.length <= 11 : false;
          const formattedDoc = docEmpresa ? formatDocument(docEmpresa, isDocPF) : '-';

          return (
            <Box
              key={idx}
              className="participacao-card-item avoid-break"
              sx={{
                p: 2.5,
                borderRadius: '12px',
                border: '1px solid #ece7f2',
                backgroundColor: '#faf8fc',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: '#723989',
                  boxShadow: '0 4px 12px rgba(114, 57, 137, 0.06)',
                  backgroundColor: '#ffffff',
                },
              }}
            >
              <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} mb={2}>
                <Tooltip title={cleanDoc ? `Consultar dossiê de ${empresa.RazaoSocial}` : ''} arrow placement="top">
                  <Typography
                    variant="body1"
                    onClick={() =>
                      solicitarConsultaComCobranca(docEmpresa, empresa.IdVeles, documentoOrigem!, {
                        nome: empresa.RazaoSocial,
                        menorIdade: false,
                        pessoaRestrita: false,
                      })
                    }
                    sx={{
                      color: cleanDoc ? '#723989' : '#1e293b',
                      fontWeight: 700,
                      cursor: cleanDoc ? 'pointer' : 'default',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.6,
                      transition: 'all 0.15s ease',
                      '&:hover': cleanDoc
                        ? {
                            textDecoration: 'underline',
                            color: '#562768',
                          }
                        : {},
                    }}
                  >
                    {empresa.RazaoSocial}
                    {cleanDoc && (
                      <LaunchIcon className="no-print" sx={{ fontSize: '0.95rem', color: '#723989', opacity: 0.7 }} />
                    )}
                  </Typography>
                </Tooltip>
                {empresa.CargoSocio && (
                  <Chip
                    label={empresa.CargoSocio}
                    size="small"
                    sx={{ backgroundColor: '#f3eff7', color: '#68457c', fontWeight: 700, borderRadius: '6px', fontSize: '0.75rem' }}
                  />
                )}
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={6} sm={4}>
                  <Typography
                    variant="caption"
                    sx={{ color: '#64748b', display: 'block', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.5, fontWeight: 700 }}
                  >
                    Documento
                  </Typography>
                  <Tooltip title={cleanDoc ? `Consultar ${cleanDoc}` : ''} arrow placement="top">
                    <Typography
                      variant="body2"
                      onClick={() =>
                        solicitarConsultaComCobranca(docEmpresa, empresa.IdVeles, documentoOrigem!, {
                          nome: empresa.RazaoSocial,
                          menorIdade: false,
                          pessoaRestrita: false,
                        })
                      }
                      sx={{
                        color: cleanDoc ? '#723989' : '#1e293b',
                        fontWeight: 600,
                        cursor: cleanDoc ? 'pointer' : 'default',
                        display: 'inline-block',
                        transition: 'all 0.15s ease',
                        '&:hover': cleanDoc
                          ? {
                              textDecoration: 'underline',
                              color: '#562768',
                            }
                          : {},
                      }}
                    >
                      {formattedDoc}
                    </Typography>
                  </Tooltip>
                </Grid>

                {empresa.Participacao != null && (
                  <Grid item xs={6} sm={4}>
                    <Typography
                      variant="caption"
                      sx={{ color: '#64748b', display: 'block', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.5, fontWeight: 700 }}
                    >
                      % Participação
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#723989', fontWeight: 700 }}>
                      {empresa.Participacao}%
                    </Typography>
                  </Grid>
                )}

                {empresa.DataSociedade && (
                  <Grid item xs={6} sm={4}>
                    <Typography
                      variant="caption"
                      sx={{ color: '#64748b', display: 'block', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.5, fontWeight: 700 }}
                    >
                      Data de Entrada
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#1e293b', fontWeight: 600 }}>
                      {formatDateSafe(empresa.DataSociedade)}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
          );
        })}
      </Box>
    </BaseCard>
  );
};
